import { RouteConfig } from 'vue-router';
import Sets from '@/views/Sets/Sets.vue';
import analytics from './analytics';
import orders from './orders';
import affiliates from './affiliates';
import { getEtsyAccessToken } from '@/api/shopify/etsy';
import linkProduct from './linkProduct';
import products from './products';
import { getStoreInstance } from '@/store';
import { AccessRoles } from '@extTypes/enums/roles';

const postExternalAuthResult = (provider: 'google' | 'etsy' | 'tiktok') => {
    const parameters = new URLSearchParams(window.location.search);

    // The Customily tab was closed, or the browser cut the link to it: nothing is waiting for the result
    if (!window.opener) {
        document.body.textContent =
            'This sign in window lost its connection to Customily. Please close it and try again.';
        return;
    }

    window.opener.postMessage(
        {
            action: 'external-auth-completed',
            provider,
            code: parameters.get('code')?.toString() || '',
            state: parameters.get('state')?.toString() || '',
            error: parameters.get('error')?.toString() || ''
        },
        `${process.env.VUE_APP_CUSTOMILY_BASE_URL}`
    );
};

const redirectIfCollectiveRetail = async (_to: unknown, _from: unknown, next: (path?: string) => void) => {
    const store = await getStoreInstance();
    const roles = store.state.user?.customilyUser?.AccessRoles?.Serialized || '';
    if (roles.split(',').includes(AccessRoles.COLLECTIVE_RETAIL)) {
        next('/products');
    } else {
        next();
    }
};

const routes: RouteConfig[] = [
    {
        path: '/',
        component: () => import('@/views/Main.vue'),
        meta: { requireStore: true },
        children: [
            {
                path: '',
                name: 'Home',
                component: () => import(/* webpackChunkName: "home" */ '@/views/Home/Home.vue'),
                beforeEnter: redirectIfCollectiveRetail
            },
            {
                path: '/authError',
                name: 'Authentication Error',
                component: () => import(/* webpackChunkName: "authError" */ '@/views/AuthError.vue')
            },
            {
                path: '/settings',
                name: 'Settings',
                component: () => import(/* webpackChunkName: "settings" */ '@/views/Settings/Settings.vue')
            },
            {
                path: '/sets',
                name: 'Sets',
                component: Sets,
                beforeEnter: async (_to, _from, next) => {
                    // Preload set page
                    import(/* webpackChunkName: "sets" */ '@/views/Set/Set.vue');
                    await redirectIfCollectiveRetail(_to, _from, next);
                }
            },
            {
                path: '/set/:optionSetId?',
                name: 'Set',
                props: true,
                component: () => import(/* webpackChunkName: "set" */ '@/views/Set/Set.vue'),
                beforeEnter: redirectIfCollectiveRetail
            },
            {
                path: '/sets/create',
                name: 'CreateOptionSet',
                component: () => import(/* webpackChunkName: "createSet" */ '@/views/CreateSet/CreateSet.vue'),
                beforeEnter: redirectIfCollectiveRetail
            },
            ...products,
            {
                path: '/podSelection',
                name: 'PODSelection',
                component: () => import(/* webpackChunkName: "podSelection" */ '@/views/Providers/Providers.vue'),
                beforeEnter: redirectIfCollectiveRetail
            },
            {
                path: '/productsSelection/:productId?/:providerId?',
                name: 'ProductSelection',
                props: true,
                component: () =>
                    import(
                        /* webpackChunkName: "productSelection" */ '@/views/StoreProductSelection/StoreProductSelection.vue'
                    )
            },
            {
                path: '/printful/:podId?',
                name: 'Printful',
                component: () => import(/* webpackChunkName: "printful" */ '@/views/Providers/Printful/Printful.vue')
            },
            {
                path: '/printify/:podId?',
                name: 'Printify',
                component: () => import(/* webpackChunkName: "printify" */ '@/views/Providers/Printify/Printify.vue')
            },
            {
                path: '/manualPod/:podId',
                name: 'manualPod',
                component: () =>
                    import(/* webpackChunkName: "manualPod" */ '@/views/Providers/ManualProviders/ManualProviders.vue')
            },

            {
                path: '/designSelection',
                name: 'DesignSelection',
                component: () =>
                    import(/* webpackChunkName: "designSelection" */ '@/views/DesignSelection/DesignSelection.vue')
            },
            {
                path: '/designLibrary',
                name: 'DesignLibrary',
                component: () =>
                    import(/* webpackChunkName: "designLibrary" */ '@/views/DesignLibrary/DesignLibrary.vue')
            },
            {
                path: '/import-customily-designs',
                name: 'ImportCustomilyDesigns',
                component: () =>
                    import(
                        /* webpackChunkName: "importCustomilyDesigns" */ '@/views/ImportDesigns/CustomilyDesigns.vue'
                    )
            },
            {
                path: '/create/template/customily-designs',
                name: 'CreateTemplateCustomilyDesigns',
                meta: { importTemplateOnly: true },
                component: () =>
                    import(
                        /* webpackChunkName: "CreateTemplateCustomilyDesigns" */ '@/views/ImportDesigns/CustomilyDesigns.vue'
                    )
            }
        ]
    },
    {
        path: '/productBuilder',
        component: () => import('@/components/shared/EmptyWrapper.vue'),
        children: [
            {
                path: '',
                name: 'ProductBuilder',
                props: true,
                component: () =>
                    import(
                        /* webpackChunkName: "ProductBuilder" */ '@/views/Providers/ProductBuilder/ProductBuilder.vue'
                    )
            }
        ]
    },
    ...analytics,
    ...affiliates,
    ...orders,
    ...linkProduct,
    {
        path: '/shPaymentConfirmed/:storeUrl',
        name: 'Shopify Payment Confirmed',
        component: () =>
            import(/* webpackChunkName: "paymentConfirmed" */ '@/views/PaymentConfirmed/PaymentConfirmed.vue'),
        meta: { isOpen: true }
    },
    {
        path: '/product-live-preview',
        name: 'ProductLivePreview',
        component: () => import(/* webpackChunkName: "ScriptLivePreview" */ '@/views/ScriptLivePreview.vue'),
        meta: { omitClasses: true }
    },
    {
        path: '/oauth-redirect',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        async beforeEnter(_to, _from, next) {
            const parameters = new URLSearchParams(window.location.search);
            const code = parameters.get('code')?.toString();
            const state = parameters.get('state')?.toString() || '';
            if (code) await getEtsyAccessToken(code, state);
            next({ name: 'PODSelection' });
        }
    },
    {
        path: '/canva-oauth-redirect',
        beforeEnter() {
            const parameters = new URLSearchParams(window.location.search);
            const code = parameters.get('code')?.toString() || '';

            window.opener.postMessage(
                {
                    code,
                    action: 'canva-authorization-completed'
                },
                `${process.env.VUE_APP_CUSTOMILY_BASE_URL}/createProduct1`
            );
        }
    },
    {
        path: '/google-oauth-redirect',
        beforeEnter: () => postExternalAuthResult('google')
    },
    {
        path: '/etsy-signin-redirect',
        beforeEnter: () => postExternalAuthResult('etsy')
    },
    {
        // The server's TikTok callback (/api/tiktok/oauth/callback) sends core's sign in here: only its popup can hand the
        //  code to core
        path: '/tiktok-oauth-redirect',
        beforeEnter() {
            postExternalAuthResult('tiktok');
            if (window.opener) {
                // Customily closes this window once it gets the result; this text stays only if it never does
                document.body.textContent =
                    'Finishing your TikTok sign in. If this window stays open, close it and try again.';
            }
        }
    }
];

export default routes;
